/**
 * @module columnMappings
 *
 * Column mapping utilities for Excel/CSV import column matching.
 * Provides flexible column name normalization for handling various naming conventions.
 *
 * **Architecture role:** This module provides the column normalization layer used
 * throughout the upload pipeline. The key function is `createColumnNormalizer`,
 * which creates a reusable function that maps raw file headers to canonical
 * UPPER_CASE field names.
 *
 * **Normalization algorithm:** All column names are normalized by:
 * 1. Converting to lowercase
 * 2. Trimming whitespace
 * 3. Replacing underscores, hyphens, and multiple spaces with single spaces
 *
 * This means `"Loan Name"`, `"LOAN_NAME"`, `"loan-name"`, and `"loan  name"`
 * all normalize to the same lookup key: `"loan name"`.
 *
 * **Integration:** Apps should define their own field mappings (via `fieldDefs`
 * JSON files or alias maps) and pass them through `buildHeaderMappings` or
 * `buildHeaderMappingsFromAliases` (from `headerMappingBuilder.js`) to produce
 * the mappings object, then pass that to `createColumnNormalizer`.
 *
 * @example <caption>Integration with headerMappingBuilder</caption>
 * import { createColumnNormalizer } from '@1823-partners/core';
 * import { buildHeaderMappings } from '@1823-partners/core';
 * import creFields from '../data/creFields.json';
 *
 * const mappings = buildHeaderMappings(creFields);
 * const normalizer = createColumnNormalizer(mappings);
 *
 * // Use in useFileUploadProcessor:
 * const { processFile } = useFileUploadProcessor({
 *   columnNormalizer: normalizer,
 *   // ...
 * });
 */

/**
 * Creates a column name normalizer function with custom mappings.
 *
 * The returned function normalizes any column name string by lowercasing,
 * trimming, and collapsing separators to spaces, then looks up the normalized
 * key in the provided mappings. If found, the mapped value (canonical field name)
 * is returned. If not found, the **original** column name is returned unchanged.
 *
 * **Why original is returned on miss:** This preserves unknown columns from the
 * uploaded file so they can be inspected or passed through. The upload pipeline
 * ignores unmapped columns when `uploadableFields` is set.
 *
 * @param {Object<string, string>} mappings - Field mappings where keys are
 *   normalized input variations (lowercase-with-spaces) and values are the
 *   target field names (typically UPPER_CASE_WITH_UNDERSCORES). Build this
 *   with `buildHeaderMappings()` or `buildHeaderMappingsFromAliases()`.
 * @returns {Function} A normalizer function with signature
 *   `(columnName: string) => string`. Returns the canonical field name if
 *   a mapping exists, or the original column name if not.
 *
 * @example <caption>Basic usage</caption>
 * const myMappings = {
 *   'price file': 'PRICE_FILE',
 *   'data file': 'PRICE_FILE',
 *   'broker': 'BROKER',
 *   'broker name': 'BROKER',
 *   'cusip': 'CUSIP',
 * };
 * const normalizer = createColumnNormalizer(myMappings);
 *
 * normalizer('Broker Name');   // 'BROKER'  (normalized to 'broker name', found in mappings)
 * normalizer('Price_File');    // 'PRICE_FILE'  (normalized to 'price file', found)
 * normalizer('CUSIP');         // 'CUSIP'  (normalized to 'cusip', found)
 * normalizer('Unknown');       // 'Unknown'  (not in mappings, returned as-is)
 * normalizer('');              // ''
 * normalizer(null);            // ''
 */
export const createColumnNormalizer = (mappings = {}) => {
  return (columnName) => {
    if (!columnName) return '';

    const normalized = columnName
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[_\s-]+/g, ' ') // Replace underscores, spaces, and hyphens with spaces
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space

    return mappings[normalized] || columnName;
  };
};

/**
 * Normalizes a column name using provided mappings (one-shot convenience function).
 *
 * Creates a temporary normalizer and applies it to the column name. If you need
 * to normalize multiple columns with the same mappings, use `createColumnNormalizer`
 * instead to avoid recreating the normalizer on each call.
 *
 * @param {string} columnName - The original column name from the imported file
 * @param {Object<string, string>} [mappings={}] - Field mappings (same format as
 *   `createColumnNormalizer`)
 * @returns {string} The normalized column name or original if no mapping found
 *
 * @example
 * const mappings = { 'custom field': 'CUSTOM' };
 * normalizeColumnName('Custom_Field', mappings); // 'CUSTOM'
 * normalizeColumnName('Unknown', mappings);       // 'Unknown'
 */
export const normalizeColumnName = (columnName, mappings = {}) => {
  const normalizer = createColumnNormalizer(mappings);
  return normalizer(columnName);
};

/**
 * Normalizes all column names in a header row.
 *
 * Applies the normalizer to each header in the array. Useful for transforming
 * an entire row of headers at once (e.g., the first row of a parsed spreadsheet).
 *
 * @param {Array<string>} headers - Array of column header names from the file
 * @param {Object<string, string>} [mappings={}] - Field mappings
 * @returns {Array<string>} Array of normalized column names (same length as input)
 *
 * @example
 * const mappings = { 'cusip': 'CUSIP', 'book yield': 'BOOK_YIELD' };
 * normalizeHeaders(['CUSIP', 'Book Yield', 'Notes'], mappings);
 * // ['CUSIP', 'BOOK_YIELD', 'Notes']
 */
export const normalizeHeaders = (headers, mappings = {}) => {
  const normalizer = createColumnNormalizer(mappings);
  return headers.map((header) => normalizer(header));
};

/**
 * Creates a mapping object from original headers to normalized headers.
 *
 * Returns an object where keys are the original header strings and values are
 * the normalized (mapped) header strings. Useful for debugging or displaying
 * a column mapping preview to the user.
 *
 * @param {Array<string>} headers - Array of original column header names
 * @param {Object<string, string>} [mappings={}] - Field mappings
 * @returns {Object<string, string>} Object mapping original headers to normalized names
 *
 * @example
 * const mappings = { 'cusip': 'CUSIP', 'book yield': 'BOOK_YIELD' };
 * createHeaderMapping(['CUSIP', 'Book Yield', 'Notes'], mappings);
 * // { 'CUSIP': 'CUSIP', 'Book Yield': 'BOOK_YIELD', 'Notes': 'Notes' }
 */
export const createHeaderMapping = (headers, mappings = {}) => {
  const normalizer = createColumnNormalizer(mappings);
  const mapping = {};
  headers.forEach((header) => {
    mapping[header] = normalizer(header);
  });
  return mapping;
};

/**
 * Transforms a row object's keys using a column normalizer.
 *
 * Takes a row object with original file header keys and returns a new object
 * with normalized keys. The values are copied as-is.
 *
 * **Note:** If two original keys normalize to the same target key, the last
 * one wins (standard object property behavior).
 *
 * @param {Object} row - Row object with original column names as keys
 * @param {Function} normalizer - Column normalizer function created by
 *   `createColumnNormalizer`
 * @returns {Object} New row object with normalized column names as keys
 *
 * @example
 * const normalizer = createColumnNormalizer({ 'cusip': 'CUSIP', 'book yield': 'BOOK_YIELD' });
 * normalizeRowKeys({ 'CUSIP': '14913UAN0', 'Book Yield': 3.5 }, normalizer);
 * // { 'CUSIP': '14913UAN0', 'BOOK_YIELD': 3.5 }
 */
export const normalizeRowKeys = (row, normalizer) => {
  const normalizedRow = {};
  Object.entries(row).forEach(([key, value]) => {
    const normalizedKey = normalizer(key);
    normalizedRow[normalizedKey] = value;
  });
  return normalizedRow;
};

/**
 * Normalizes boolean-like values to actual booleans.
 *
 * Handles various representations of true/false commonly found in Excel/CSV imports.
 * This is useful as a field transform for boolean columns.
 *
 * **Truthy values** (return `true`): `'true'`, `'yes'`, `'y'`, `'1'`, `'on'`,
 * `'enabled'`, `'active'`, `true` (boolean), `1` (number).
 *
 * **Everything else** returns `false`, including `null`, `undefined`, `''`,
 * `'false'`, `'no'`, `'n'`, `'0'`, `'off'`, etc.
 *
 * **Comparison with `coerceNumeric`:** Use `normalizeBooleanValue` when you need
 * an actual `boolean` (e.g., for a checkbox column). Use `coerceNumeric` from
 * `fieldTransforms.js` when you need `1`/`0` (e.g., for a numeric flag field).
 *
 * @param {*} value - The value to normalize
 * @returns {boolean} The normalized boolean value
 *
 * @example
 * normalizeBooleanValue('Yes');      // true
 * normalizeBooleanValue('N');        // false
 * normalizeBooleanValue(1);          // true
 * normalizeBooleanValue(0);          // false
 * normalizeBooleanValue('');         // false
 * normalizeBooleanValue(null);       // false
 * normalizeBooleanValue('active');   // true
 * normalizeBooleanValue('disabled'); // false
 *
 * @example <caption>Usage as a field transform</caption>
 * import { normalizeBooleanValue } from '@1823-partners/core';
 *
 * const processor = createSheetProcessor({
 *   fieldTransforms: {
 *     SECURITY_LIST: normalizeBooleanValue,
 *     ADDITIONS: normalizeBooleanValue,
 *   },
 * });
 */
export const normalizeBooleanValue = (value) => {
  if (value === null || value === undefined || value === '') return false;

  const stringValue = value.toString().toLowerCase().trim();

  // Truthy values that should resolve to true
  const truthyValues = ['true', 'yes', 'y', '1', 'on', 'enabled', 'active'];

  return truthyValues.includes(stringValue) || value === true || value === 1;
};

export default {
  createColumnNormalizer,
  normalizeColumnName,
  normalizeHeaders,
  createHeaderMapping,
  normalizeRowKeys,
  normalizeBooleanValue,
};
